import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Button } from 'react-aria-components';
import { AccessibilityDialog } from './AccessibilityDialog';

interface AccessibleMenuProps {
  onAnnounce: (message: string) => void;
}

const links = [
  { label: 'Inicio', href: '#inicio' },
  { label: 'El proyecto', href: '#proyecto' },
  { label: 'Accesibilidad', href: '#accesibilidad' },
];

export function AccessibleMenu({ onAnnounce }: AccessibleMenuProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const originRef = useRef<HTMLElement | null>(null);

  const menuItems = useCallback(
    () => Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []),
    [],
  );

  const focusItem = useCallback(
    (index: number) => {
      const items = menuItems();
      if (!items.length) return;
      const next = (index + items.length) % items.length;
      setActiveIndex(next);
      items[next]?.focus();
    },
    [menuItems],
  );

  const isCompact = () => window.matchMedia('(max-width: 767.98px)').matches;

  const openMenu = useCallback(() => {
    setOpen(true);
    onAnnounce('Menú desplegado.');
    window.setTimeout(() => focusItem(activeIndex), 0);
  }, [activeIndex, focusItem, onAnnounce]);

  const closeMenu = useCallback(
    (restoreFocus = true) => {
      setOpen(false);
      onAnnounce('Menú plegado.');
      if (restoreFocus) window.setTimeout(() => toggleRef.current?.focus(), 0);
    },
    [onAnnounce],
  );

  const enterMenu = useCallback(() => {
    const activeElement = document.activeElement;
    originRef.current =
      activeElement instanceof HTMLElement && activeElement !== document.body ? activeElement : null;
    if (isCompact()) {
      openMenu();
      return;
    }
    onAnnounce('Menú principal.');
    focusItem(activeIndex);
  }, [activeIndex, focusItem, onAnnounce, openMenu]);

  const exitMenu = useCallback(() => {
    if (isCompact()) {
      closeMenu();
      return;
    }
    onAnnounce('Saliste del menú.');
    const destination = originRef.current;
    originRef.current = null;
    if (destination && document.contains(destination) && !menuRef.current?.contains(destination)) {
      destination.focus();
    } else {
      document.getElementById('main-content')?.focus();
    }
  }, [closeMenu, onAnnounce]);

  useEffect(() => {
    const onShortcut = (event: KeyboardEvent) => {
      if (
        !event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.key.toLocaleLowerCase('es') !== 'm'
      ) {
        return;
      }
      event.preventDefault();
      if (open || menuRef.current?.contains(document.activeElement)) exitMenu();
      else enterMenu();
    };
    document.addEventListener('keydown', onShortcut);
    return () => document.removeEventListener('keydown', onShortcut);
  }, [enterMenu, exitMenu, open]);

  const handleMenuKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const items = menuItems();
    if (!items.length) return;
    const current = items.indexOf(document.activeElement as HTMLElement);
    const move = (next: number) => {
      event.preventDefault();
      focusItem(next);
    };

    if (event.key === 'Escape') {
      event.preventDefault();
      exitMenu();
    } else if (event.key === 'Home') move(0);
    else if (event.key === 'End') move(items.length - 1);
    else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') move(current + 1);
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') move(current - 1);
    else if (
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey &&
      event.key.length === 1 &&
      /\S/.test(event.key)
    ) {
      const letter = event.key.toLocaleLowerCase('es');
      for (let step = 1; step <= items.length; step += 1) {
        const index = (Math.max(current, -1) + step) % items.length;
        if ((items[index]?.textContent ?? '').trim().toLocaleLowerCase('es').startsWith(letter)) {
          move(index);
          break;
        }
      }
    }
  };

  return (
    <header className="site-header">
      <nav className="navbar navbar-expand-md" aria-label="Principal">
        <div className="container">
          <a className="navbar-brand" href="#inicio" aria-label="Orun, inicio">
            <span className="brand-symbol" aria-hidden="true">O</span>
            <span>Orun</span>
          </a>

          <div className="header-actions order-md-3">
            <AccessibilityDialog />
            <Button
              ref={toggleRef}
              className="navbar-toggler"
              aria-controls="orun-main-menu"
              aria-expanded={open}
              aria-label={open ? 'Cerrar menú de navegación' : 'Abrir menú de navegación'}
              aria-haspopup="menu"
              aria-keyshortcuts="Alt+M"
              onPress={() => (open ? closeMenu() : openMenu())}
            >
              <span className="navbar-toggler-icon" />
            </Button>
          </div>

          <div
            ref={menuRef}
            id="orun-main-menu"
            className={'collapse navbar-collapse' + (open ? ' show' : '')}
            aria-keyshortcuts="Alt+M"
            onKeyDown={handleMenuKey}
          >
            <ul className="navbar-nav ms-auto" role="menu" aria-label="Principal">
              {links.map((link, index) => (
                <li className="nav-item" role="none" key={link.href}>
                  <a
                    className="nav-link"
                    role="menuitem"
                    tabIndex={activeIndex === index ? 0 : -1}
                    href={link.href}
                    onFocus={() => setActiveIndex(index)}
                    onClick={() => setOpen(false)}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
}
